import { useState, useEffect, useCallback } from 'react';
import { AnalysisStatus } from '../components/ui/AnalysisStatusMessage';
import { apiClient } from '../lib/api/client';

interface UseAnalysisStatusProps {
  candidateId?: string;
  requirementId?: string;
  enabled?: boolean;
}

export const useAnalysisStatus = ({ 
  candidateId, 
  requirementId, 
  enabled = true 
}: UseAnalysisStatusProps) => {
  const [comprehensiveStatus, setComprehensiveStatus] = useState<AnalysisStatus | null>(null);
  const [jdCvStatus, setJdCvStatus] = useState<AnalysisStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // Poll for analysis status
  const pollAnalysisStatus = useCallback(async () => {
    if (!candidateId || !enabled) return;

    try {
      // Get candidate data to check analysis status
      const candidateData = await apiClient.getCandidate(candidateId);
      
      if (candidateData) {
        // Check comprehensive analysis status
        const comprehensiveStatus = candidateData.comprehensive_analysis_status;
        if (comprehensiveStatus && comprehensiveStatus !== 'none') {
          setComprehensiveStatus({
            type: 'comprehensive',
            status: comprehensiveStatus as AnalysisStatus['status'],
            message: getStatusMessage('comprehensive', comprehensiveStatus),
            retryCount: candidateData.comprehensive_analysis_retry_count || 0
          });
        }

        // Check JD vs CV analysis status
        const jdCvStatus = candidateData.jd_cv_analysis_status;
        if (jdCvStatus && jdCvStatus !== 'none') {
          setJdCvStatus({
            type: 'jd_cv',
            status: jdCvStatus as AnalysisStatus['status'],
            message: getStatusMessage('jd_cv', jdCvStatus),
            retryCount: candidateData.jd_cv_analysis_retry_count || 0
          });
        }

        // Continue polling if any analysis is still processing
        if (comprehensiveStatus === 'processing' || jdCvStatus === 'processing') {
          setTimeout(pollAnalysisStatus, 5000); // Poll every 5 seconds
          setIsPolling(true);
        } else {
          setIsPolling(false);
        }
      }
    } catch (error) {
      console.error('Error polling analysis status:', error);
      // Continue polling on error
      setTimeout(pollAnalysisStatus, 10000); // Poll every 10 seconds on error
    }
  }, [candidateId, enabled]);

  // Start polling when candidateId changes
  useEffect(() => {
    if (candidateId && enabled) {
      setIsPolling(true);
      pollAnalysisStatus();
    } else {
      setIsPolling(false);
    }
  }, [candidateId, enabled, pollAnalysisStatus]);

  // Retry comprehensive analysis
  const retryComprehensiveAnalysis = useCallback(async () => {
    if (!candidateId) return;

    try {
      setComprehensiveStatus(prev => prev ? {
        ...prev,
        status: 'processing',
        message: 'Retrying comprehensive analysis...'
      } : null);

      await apiClient.retryComprehensiveAnalysis(candidateId);
      
      // Start polling again
      setTimeout(pollAnalysisStatus, 2000);
    } catch (error) {
      console.error('Failed to retry comprehensive analysis:', error);
      setComprehensiveStatus(prev => prev ? {
        ...prev,
        status: 'failed',
        message: 'Failed to retry comprehensive analysis'
      } : null);
    }
  }, [candidateId, pollAnalysisStatus]);

  // Retry JD vs CV analysis
  const retryJdCvAnalysis = useCallback(async () => {
    if (!candidateId || !requirementId) return;

    try {
      setJdCvStatus(prev => prev ? {
        ...prev,
        status: 'processing',
        message: 'Retrying JD vs CV analysis...'
      } : null);

      await apiClient.retryJdCvAnalysis(candidateId, requirementId);
      
      // Start polling again
      setTimeout(pollAnalysisStatus, 2000);
    } catch (error) {
      console.error('Failed to retry JD vs CV analysis:', error);
      setJdCvStatus(prev => prev ? {
        ...prev,
        status: 'failed',
        message: 'Failed to retry JD vs CV analysis'
      } : null);
    }
  }, [candidateId, requirementId, pollAnalysisStatus]);

  // Dismiss status messages
  const dismissComprehensiveStatus = useCallback(() => {
    setComprehensiveStatus(null);
  }, []);

  const dismissJdCvStatus = useCallback(() => {
    setJdCvStatus(null);
  }, []);

  return {
    comprehensiveStatus: comprehensiveStatus ? {
      ...comprehensiveStatus,
      onRetry: retryComprehensiveAnalysis,
      onDismiss: dismissComprehensiveStatus
    } : null,
    jdCvStatus: jdCvStatus ? {
      ...jdCvStatus,
      onRetry: retryJdCvAnalysis,
      onDismiss: dismissJdCvStatus
    } : null,
    isPolling,
    pollAnalysisStatus
  };
};

// Helper function to get status messages
function getStatusMessage(type: 'comprehensive' | 'jd_cv', status: string): string {
  const typeLabel = type === 'comprehensive' ? 'Comprehensive' : 'JD vs CV';
  
  switch (status) {
    case 'pending':
      return `${typeLabel} analysis queued for processing`;
    case 'processing':
      return `${typeLabel} analysis in progress...`;
    case 'completed':
      return `${typeLabel} analysis completed successfully`;
    case 'failed':
      return `${typeLabel} analysis failed. Click retry to try again.`;
    default:
      return `${typeLabel} analysis status unknown`;
  }
}
